export type BrowserFamily =
  | "chrome"
  | "edge"
  | "firefox"
  | "safari"
  | "unknown";

export type BrowserInfo = {
  family: BrowserFamily;
  version: string | null;
  major: number | null;
  isChromium: boolean;
  userAgent: string;
};

export type BrowserSupportPolicy = {
  allowedFamilies: BrowserFamily[];
  minimumVersions: Partial<Record<BrowserFamily, number>>;
  warningVersions?: Partial<Record<BrowserFamily, number>>;
  enforcementDates?: Partial<Record<BrowserFamily, string>>;
  policyVersion?: string;
  overrideTtlMinutes?: number;
};

export type BrowserSupportEvaluation = {
  state: "supported" | "warning" | "blocked";
  supported: boolean;
  reason:
    | "supported"
    | "version_in_warning_window"
    | "family_not_allowed"
    | "unknown_version"
    | "version_too_low";
  minimumVersion: number | null;
  warningVersion: number | null;
  enforcementDate: string | null;
};

const EDGE_VERSION_RE = /\bEdg\/([\d.]+)/i;
const CHROME_VERSION_RE = /\b(?:Chrome|CriOS)\/([\d.]+)/i;
const FIREFOX_VERSION_RE = /\bFirefox\/([\d.]+)/i;
const SAFARI_VERSION_RE = /\bVersion\/([\d.]+).+Safari\//i;

function parseMajor(version: string | null): number | null {
  if (!version) return null;
  const first = version.split(".")[0];
  const major = Number(first);
  return Number.isFinite(major) ? major : null;
}

export function getBrowserDisplayName(family: BrowserFamily): string {
  switch (family) {
    case "chrome":
      return "Google Chrome";
    case "edge":
      return "Microsoft Edge";
    case "firefox":
      return "Mozilla Firefox";
    case "safari":
      return "Apple Safari";
    default:
      return "Unknown";
  }
}

export function isChromiumFamily(family: BrowserFamily): boolean {
  return family === "chrome" || family === "edge";
}

export function detectBrowser(userAgent?: string): BrowserInfo {
  const ua = userAgent ?? window.navigator.userAgent ?? "";
  const edgeMatch = ua.match(EDGE_VERSION_RE);
  if (edgeMatch) {
    const version = edgeMatch[1] || null;
    return {
      family: "edge",
      version,
      major: parseMajor(version),
      isChromium: true,
      userAgent: ua,
    };
  }

  const chromeMatch = ua.match(CHROME_VERSION_RE);
  if (chromeMatch) {
    const version = chromeMatch[1] || null;
    return {
      family: "chrome",
      version,
      major: parseMajor(version),
      isChromium: true,
      userAgent: ua,
    };
  }

  const firefoxMatch = ua.match(FIREFOX_VERSION_RE);
  if (firefoxMatch) {
    const version = firefoxMatch[1] || null;
    return {
      family: "firefox",
      version,
      major: parseMajor(version),
      isChromium: false,
      userAgent: ua,
    };
  }

  const safariMatch = ua.match(SAFARI_VERSION_RE);
  if (safariMatch) {
    const version = safariMatch[1] || null;
    return {
      family: "safari",
      version,
      major: parseMajor(version),
      isChromium: false,
      userAgent: ua,
    };
  }

  return {
    family: "unknown",
    version: null,
    major: null,
    isChromium: false,
    userAgent: ua,
  };
}

export function isLocalDevHostname(hostname: string): boolean {
  const lower = hostname.toLowerCase();
  return (
    lower === "localhost" ||
    lower === "127.0.0.1" ||
    lower.endsWith(".localhost") ||
    lower.endsWith(".local")
  );
}

export function isAutomationUserAgent(userAgent: string): boolean {
  return /HeadlessChrome|Playwright|puppeteer|CursorBrowser|Electron/i.test(
    userAgent,
  );
}

export function shouldBypassBrowserGate(
  location?: Pick<Location, "hostname">,
  userAgent?: string,
): boolean {
  if (
    String(import.meta.env.VITE_SKIP_BROWSER_GATE || "").toLowerCase() === "true"
  ) {
    return true;
  }

  // CI/smoke can force the gate on even when Vite is in DEV on localhost.
  if (
    String(import.meta.env.VITE_ENFORCE_BROWSER_GATE || "").toLowerCase() ===
    "true"
  ) {
    return false;
  }

  const ua =
    userAgent ??
    (typeof navigator !== "undefined" ? navigator.userAgent : "");
  if (isAutomationUserAgent(ua)) {
    return true;
  }

  if (import.meta.env.DEV && location) {
    return isLocalDevHostname(location.hostname);
  }

  return false;
}

export function evaluateBrowserSupport(
  browser: BrowserInfo,
  policy: BrowserSupportPolicy,
): BrowserSupportEvaluation {
  const warningVersion = policy.warningVersions?.[browser.family] ?? null;
  const enforcementDate = policy.enforcementDates?.[browser.family] ?? null;

  if (!policy.allowedFamilies.includes(browser.family)) {
    return {
      state: "blocked",
      supported: false,
      reason: "family_not_allowed",
      minimumVersion: null,
      warningVersion,
      enforcementDate,
    };
  }

  const minVersion = policy.minimumVersions[browser.family] ?? null;
  if (minVersion == null) {
    return {
      state: "supported",
      supported: true,
      reason: "supported",
      minimumVersion: null,
      warningVersion,
      enforcementDate,
    };
  }

  if (browser.major == null) {
    return {
      state: "blocked",
      supported: false,
      reason: "unknown_version",
      minimumVersion: minVersion,
      warningVersion,
      enforcementDate,
    };
  }

  if (browser.major < minVersion) {
    return {
      state: "blocked",
      supported: false,
      reason: "version_too_low",
      minimumVersion: minVersion,
      warningVersion,
      enforcementDate,
    };
  }

  if (warningVersion != null && warningVersion > minVersion) {
    if (browser.major < warningVersion) {
      return {
        state: "warning",
        supported: true,
        reason: "version_in_warning_window",
        minimumVersion: minVersion,
        warningVersion,
        enforcementDate,
      };
    }
  }

  return {
    state: "supported",
    supported: true,
    reason: "supported",
    minimumVersion: minVersion,
    warningVersion,
    enforcementDate,
  };
}
